import React from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Select,
  Option,
  Tooltip,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  IconButton,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { Question, Trash as Delete, Plus as Add } from '@phosphor-icons/react';
import { proposalsApi } from '../../lib/proposalsApi';
import { useIsMobile } from '../../hooks/useIsMobile';
import { DebouncedInput } from '../common/DebouncedInput';
import type { ProposalDetail } from '../../types/proposals';

/**
 * Parses a typed number, falling back when the field is empty or half-typed.
 * `0` is a legitimate value, so this cannot lean on `||`.
 */
const toAmount = (value: string, fallback: number): number => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// Optional Upgrades Editor
export const OptionalUpgradesEditor: React.FC<{
  upgrades: ProposalDetail['optional_upgrades'];
  proposalId: number;
  onRefresh: () => void;
}> = ({ upgrades, proposalId, onRefresh }) => {
  const addUpgradeMutation = useMutation({
    mutationFn: () =>
      proposalsApi.createUpgrade({
        proposal: proposalId,
        name: 'New Upgrade',
        description: '',
        quantity: 1,
        unit: 'ea',
        unit_price: 0,
      }),
    onSuccess: onRefresh,
  });

  const updateUpgradeMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => proposalsApi.updateUpgrade(id, data),
    onSuccess: onRefresh,
  });

  const deleteUpgradeMutation = useMutation({
    mutationFn: (id: number) => proposalsApi.deleteUpgrade(id),
    onSuccess: onRefresh,
  });

  const isMobile = useIsMobile();

  const requirement = (upgrade: ProposalDetail['optional_upgrades'][number]) => (
    <Select size="sm" aria-label={`Charge type for ${upgrade.name}`} value={upgrade.is_required ? 'required' : 'optional'}
      disabled={updateUpgradeMutation.isPending}
      onChange={(_, value) => value && updateUpgradeMutation.mutate({ id: upgrade.id, data: { is_required: value === 'required' } })}>
      <Option value="optional">Optional</Option><Option value="required">Required</Option>
    </Select>
  );
  return (
    <Box>
      <Typography level="title-lg" sx={{ mb: 2 }}>
        Add-ons & Fees
      </Typography>
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
        Required charges are included in every package. Optional upgrades are added only when the customer selects them.
        <Tooltip title="Use Required for project costs such as steep pitch or access fees. Customers see these charges in every package total and cannot remove them.">
          <IconButton size="sm" aria-label="About required charges"><Question /></IconButton>
        </Tooltip>
      </Typography>

      {(updateUpgradeMutation.isError || addUpgradeMutation.isError || deleteUpgradeMutation.isError) && <Alert color="danger">Could not save the change. Please try again.</Alert>}
      {[true, false].map(required => {
        const items = upgrades.filter(item => Boolean(item.is_required) === required);
        return <Box key={String(required)} sx={{ mb: 3 }}>
        <Typography level="title-md" component="h3" sx={{ mb: 1 }}>{required ? 'Required charges' : 'Optional upgrades'}</Typography>
        {!items.length && <Typography level="body-sm" sx={{ mb: 2 }}>No {required ? 'required charges' : 'optional upgrades'} yet.</Typography>}
      {items.length > 0 && (isMobile ? (
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {items.map((upgrade) => (
            <Card key={upgrade.id} variant="outlined" sx={{ p: 2, gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography level="title-sm" sx={{ flex: 1, minWidth: 0 }}>
                  {upgrade.name || 'Untitled upgrade'}
                </Typography>
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => deleteUpgradeMutation.mutate(upgrade.id)}
                >
                  <Delete size={18} />
                </IconButton>
              </Box>
              {requirement(upgrade)}
              <FormControl size="sm">
                <FormLabel>Name</FormLabel>
                <DebouncedInput
                  value={upgrade.name}
                  onCommit={(name) => updateUpgradeMutation.mutate({ id: upgrade.id, data: { name } })}
                />
              </FormControl>
              <FormControl size="sm">
                <FormLabel>Description</FormLabel>
                <DebouncedInput
                  value={upgrade.description}
                  onCommit={(description) =>
                    updateUpgradeMutation.mutate({ id: upgrade.id, data: { description } })
                  }
                />
              </FormControl>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl size="sm" sx={{ flex: 1 }}>
                  <FormLabel>Qty</FormLabel>
                  <DebouncedInput
                    type="number"
                    value={upgrade.quantity}
                    onCommit={(value) =>
                      updateUpgradeMutation.mutate({
                        id: upgrade.id,
                        data: { quantity: toAmount(value, 1) },
                      })
                    }
                  />
                </FormControl>
                <FormControl size="sm" sx={{ flex: 1 }}>
                  <FormLabel>Unit Price</FormLabel>
                  <DebouncedInput
                    type="number"
                    startDecorator="$"
                    value={upgrade.unit_price}
                    onCommit={(value) =>
                      updateUpgradeMutation.mutate({
                        id: upgrade.id,
                        data: { unit_price: toAmount(value, 0) },
                      })
                    }
                  />
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  Total
                </Typography>
                <Typography level="body-sm" fontWeight="lg">
                  ${upgrade.total_price.toLocaleString()}
                </Typography>
              </Box>
            </Card>
          ))}
        </Stack>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'hidden', mb: 2 }}>
          <Table size="sm">
            <thead>
              <tr>
                <th style={{ width: 125 }}>Charge type</th>
                <th>Name</th>
                <th>Description</th>
                <th style={{ width: 80 }}>Qty</th>
                <th style={{ width: 100 }}>Unit Price</th>
                <th style={{ width: 100 }}>Total</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((upgrade) => (
                <tr key={upgrade.id}>
                  <td>{requirement(upgrade)}</td>
                  <td>
                    <DebouncedInput
                      size="sm"
                      variant="plain"
                      value={upgrade.name}
                      onCommit={(name) =>
                        updateUpgradeMutation.mutate({ id: upgrade.id, data: { name } })
                      }
                    />
                  </td>
                  <td>
                    <DebouncedInput
                      size="sm"
                      variant="plain"
                      value={upgrade.description}
                      onCommit={(description) =>
                        updateUpgradeMutation.mutate({ id: upgrade.id, data: { description } })
                      }
                    />
                  </td>
                  <td>
                    <DebouncedInput
                      size="sm"
                      type="number"
                      value={upgrade.quantity}
                      onCommit={(value) =>
                        updateUpgradeMutation.mutate({
                          id: upgrade.id,
                          data: { quantity: toAmount(value, 1) },
                        })
                      }
                    />
                  </td>
                  <td>
                    <DebouncedInput
                      size="sm"
                      type="number"
                      startDecorator="$"
                      value={upgrade.unit_price}
                      onCommit={(value) =>
                        updateUpgradeMutation.mutate({
                          id: upgrade.id,
                          data: { unit_price: toAmount(value, 0) },
                        })
                      }
                    />
                  </td>
                  <td>
                    <Typography level="body-sm" fontWeight="md">
                      ${upgrade.total_price.toLocaleString()}
                    </Typography>
                  </td>
                  <td>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="danger"
                      onClick={() => deleteUpgradeMutation.mutate(upgrade.id)}
                    >
                      <Delete size={18} />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      ))}
      </Box>; })}

      <Button
        variant="soft"
        startDecorator={<Add />}
        onClick={() => addUpgradeMutation.mutate()}
        loading={addUpgradeMutation.isPending}
      >
        Add item
      </Button>
    </Box>
  );
};
